import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { A } from '@adornis/base/env-info.js';
import { Int, ID } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { scopedCollection } from '../server/scoped-collection.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet, type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import { Price } from '@adornis/payments/db/prices.js';
import { of, switchMap, map } from 'rxjs';
import { Bundle } from '../entities/Bundle.js';
import { BundlePrice } from '../entities/BundlePrice.js';
import { BHFilter } from '../entities/bh-filter.js';
import { Product } from '../entities/products.js';
import { mongo } from '../server/mongo.js';
import { getProductsByIDs } from './product.js';

export function bundleDiscountInPercent(
  bundle: NarrowedEntity<Bundle, { products: { hasPrice: 1; prices: { unitAmount: 1 } } }>,
): number {
  if (!(bundle as Bundle).getUnitAmount) return 0;
  const priceBefore = (bundle.products ?? []).reduce(
    (val, product) => (val += product.hasPrice ? (product.prices[0]?.unitAmount ?? 0) : 0),
    0,
  );
  if (priceBefore === 0) return 0;
  return (1 - (bundle as Bundle).getCalcAmount / priceBefore) * 100;
}

async function livediscountInPercent(bundle: NarrowedEntity<Bundle, { productIDs: 1 }>): Promise<number> {
  const products = await getProductsByIDs(bundle.productIDs)({ hasPrice: 1, prices: { unitAmount: 1 } });

  if (!(bundle as Bundle).getUnitAmount) return 0;
  const priceBefore = products.reduce(
    (val, product) => (val += product.hasPrice ? (product.prices[0]?.unitAmount ?? 0) : 0),
    0,
  );
  if (priceBefore === 0) return 0;
  return (1 - (bundle as Bundle).getCalcAmount / priceBefore) * 100;
}

export async function updateCheckProductPrices(bundle: NarrowedEntity<Bundle, { _id: 1; productIDs: 1 }>) {
  let products = (await getProductsByIDs(bundle.productIDs)({
    _id: 1,
    prices: selectionSet(() => Price, 2),
    hasPrice: 1,
  })) as Product[];
  const productCollection = mongo().collection<EntityData<Product>>(Product._collectionName);
  products = products.filter(product => (product.prices[0]?.unitAmount ?? 0) > 0);

  let cost = 0;
  for (const [index, product] of products.entries()) {
    let price = product.prices.find(price => price instanceof BundlePrice && price.bundleID === bundle._id);
    const discountPrice = product.hasPrice
      ? (product.prices[0]?.unitAmount ?? 0) * (1 - (await livediscountInPercent(bundle)) / 100)
      : 0;
    if (price) {
      price.unitAmount = discountPrice;
    } else {
      price = new BundlePrice({ _id: A.getGloballyUniqueID(), bundleID: bundle._id, unitAmount: discountPrice });
      product.prices.push(price);
    }

    cost += price.unitAmount;

    if (index === products.length - 1) {
      price.unitAmount = price.unitAmount - (cost - (bundle as Bundle).getCalcAmount);
    }

    await productCollection.updateOne(
      { _id: product._id },
      {
        $set: {
          prices: product.prices.map(price => price.toJSON()),
        },
      },
    );
  }
}

export const getAllBundlesByInstitution = registerQuery({
  operationName: 'getAllBundlesByInstitution',
  type: () => [Bundle],
  params: [{ name: 'institutionID', type: () => String }],
  resolve: institutionID => {
    return async (gqlFields: BaseQLSelectionSet<Bundle>) => {
      const collection = await scopedCollection<Bundle>(Bundle._class);
      const result = await collection.find<Bundle>({ institutionID }).toArray();
      return result;
    };
  },
});

export const subscribeAllBundlesByInstitution = registerSubscription({
  operationName: 'subscribeAllBundlesByInstitution',
  type: () => [Bundle],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'institutionID', type: () => BHFilter },
  ],
  resolve: (skip, limit, filter) => {
    return (gqlFields: BaseQLSelectionSet<Bundle>) => {
      if (!filter.institutionID) return of([]);
      return watchEntireCollection(Bundle._collectionName).pipe(
        switchMap(async () => {
          const colleciton = await scopedCollection(Bundle._class);
          return colleciton.find<Bundle>({ institutionID: filter.institutionID }, { skip, limit }).toArray();
        }),
      );
    };
  },
});

export const getValidBundlesForProducts = registerQuery({
  operationName: 'getValidBundlesForProducts',
  type: () => [Bundle],
  params: [{ name: 'productIDs', type: () => [String] }],
  resolve: productIDs => {
    return async <SelSet extends BaseQLSelectionSet<Bundle>>(gqlFields: SelSet) => {
      const collection = await scopedCollection<Bundle>(Bundle._class);
      const result = await collection
        .find<Bundle>({
          isActive: true,
          $expr: {
            $and: [
              { $setIsSubset: ['$productIDs', productIDs] },
              { $lt: ['$publishDate', new Date()] },
              { $gt: [{ $size: '$productIDs' }, 0] },
            ],
          },
        })
        .toArray();
      return result as NarrowedEntity<Bundle, SelSet>[];
    };
  },
});

export function getSelectedBundles(
  bundles: NarrowedEntityData<Bundle, { _id: 1; productIDs: 1; isActive: 1 }>[],
  productIDs: string[],
) {
  return bundles.filter(
    bundle => bundle.isActive && bundle.productIDs.every(productID => productIDs.includes(productID)),
  );
}

export const getBundlesByIDs = registerQuery({
  operationName: 'getBundlesByIDs',
  type: () => [Bundle],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Bundle>>(gqlFields: SelSet) =>
      readEntitiesByIDs(Bundle, ids, gqlFields),
});

export const subscribeBundlesByIDs = registerSubscription({
  operationName: 'subscribeBundlesByIDs',
  type: () => [Bundle],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Bundle>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(Bundle, ids, gqlFields),
});

export const getAllBundles = registerQuery({
  operationName: 'getAllBundles',
  type: () => [Bundle],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Bundle>>(gqlFields: SelSet) =>
      readAllEntities(Bundle, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllBundles = registerSubscription({
  operationName: 'subscribeAllBundles',
  type: () => [Bundle],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Bundle>>(gqlFields: SelSet) =>
      watchAllEntities(Bundle, gqlFields, { skip, limit, excludeIDs }),
});

export const getBundleByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<Bundle>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getBundlesByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeBundleByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<Bundle>>(gqlFields: SelSet) =>
    id ? subscribeBundlesByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
